import equal from 'fast-deep-equal';

import { InspectionSyncItemsResponse } from '../../api-contracts';
import { inspectionDB } from '../../data/database.structure';
import { DataTypesEnum } from '../../data/enums';
import {
  EquipmentPartModel,
  InspectionCategoryTypeModel,
  InspectionModel,
  ObservationModel,
  SendFailure,
} from '../../data/types';
import {
  DeliveryState,
  deliveryStateOf,
  ELIGIBLE_FOR_BACKGROUND_SEND,
} from '../../helpers/deliveryStateHelpers';
import { waitAfter } from '../../helpers/sendFailureHelpers';
import { mergeServerIdsIntoInspection } from '../../helpers/reconcileInspectionServerIds';
import { retainObservedFitments } from '../../helpers/retainObservedFitments';
import { cacheInvalidationService } from '../cacheInvalidationService';
import {
  applySyncSuccess,
  SyncSuccessItems,
} from '../inspectionSync/applySyncSuccess';

export { DeliveryState };

const PENDING_DELIVERY = new Set([
  DeliveryState.Queued,
  DeliveryState.PhotosPending,
  DeliveryState.NeedsAttention,
]);

/** Read-only until reopened; Reopen is allowed from each of them. */
const SUBMITTED = new Set([
  DeliveryState.Queued,
  DeliveryState.PhotosPending,
  DeliveryState.NeedsAttention,
  DeliveryState.Delivered,
]);

/** A photo the send converted from HEIC: its value changes from `from` to `to`. */
interface PhotoRename {
  observationExternalId: string;
  from: string;
  to: string;
}

/** A server copy in the shape `mergeServerIdsIntoInspection` reads. */
const asSyncItems = (serverCopy: InspectionModel) => ({
  inspections: [serverCopy],
  inspectionCategoryTypes: serverCopy.inspectionCategoryTypes,
  observations: serverCopy.inspectionCategoryTypes.flatMap(
    (categoryType) => categoryType.observations,
  ),
});

/**
 * Keeps the local copy, taking from the server only its ids and "photo received" for
 * photos still marked sent. A photo replaced on the device keeps its own value, because
 * the server's belongs to the old file.
 */
function applyServerIdsAndPhotoReceipts(
  local: InspectionModel,
  serverCopy: InspectionModel,
): InspectionModel {
  const serverItems = asSyncItems(serverCopy);
  const serverObservations = serverItems.observations;
  const withServerIds = mergeServerIdsIntoInspection(local, serverItems);
  if (!withServerIds) return local;

  return {
    ...local,
    id: withServerIds.id,
    inspectionCategoryTypes: withServerIds.inspectionCategoryTypes.map(
      (categoryType) => ({
        ...categoryType,
        observations: categoryType.observations.map((observation) => {
          if (
            observation.observableItem?.dataTypeId !== DataTypesEnum.image ||
            !observation.isSynced
          ) {
            return observation;
          }
          const serverObservation = serverObservations.find(
            (o) => o.externalId === observation.externalId,
          );
          return serverObservation
            ? { ...observation, fileReceived: serverObservation.fileReceived }
            : observation;
        }),
      }),
    ),
  };
}

/** Takes the server ids from an accepted copy; content and flags stay as they are. */
function withServerIds(
  record: InspectionModel,
  accepted: InspectionModel,
): InspectionModel {
  const merged = mergeServerIdsIntoInspection(record, asSyncItems(accepted));
  return merged ? { ...record, ...merged } : record;
}

// What the send carries, without the ids and flags its result sets.
const sentObservationFields = (observation: ObservationModel) => ({
  ...observation,
  id: 0,
  isSynced: false,
  fileReceived: false,
});
const sentCategoryFields = (categoryType: InspectionCategoryTypeModel) => ({
  ...categoryType,
  id: 0,
  isSynced: false,
  observations: [],
});
const sentInspectionFields = (inspection: InspectionModel) => ({
  inspectionDate: inspection.inspectionDate,
  assetId: inspection.assetId,
  mineLocationId: inspection.mineLocationId,
  siteId: inspection.siteId,
});

/**
 * Marks sent only the parts of the current record that still match what was sent.
 * Anything added, changed or removed during the send stays unsent, so it goes with the
 * next send.
 */
function withSentPartsAccepted(
  current: InspectionModel,
  sent: InspectionModel,
): InspectionModel {
  const sentCategories = new Map(
    sent.inspectionCategoryTypes.map((categoryType) => [
      categoryType.externalId,
      categoryType,
    ]),
  );
  return {
    ...current,
    isSynced: equal(sentInspectionFields(current), sentInspectionFields(sent)),
    inspectionCategoryTypes: current.inspectionCategoryTypes.map(
      (categoryType) => {
        const sentCategory = sentCategories.get(categoryType.externalId);
        if (!sentCategory) return { ...categoryType, isSynced: false };
        const sentObservations = new Map(
          sentCategory.observations.map((o) => [o.externalId, o]),
        );
        const currentExternalIds = new Set(
          categoryType.observations.map((o) => o.externalId),
        );
        const removedDuringSend = sentCategory.observations.some(
          (o) => !currentExternalIds.has(o.externalId),
        );
        return {
          ...categoryType,
          isSynced:
            !removedDuringSend &&
            equal(
              sentCategoryFields(categoryType),
              sentCategoryFields(sentCategory),
            ),
          observations: categoryType.observations.map((observation) => {
            const sentObservation = sentObservations.get(
              observation.externalId,
            );
            if (!sentObservation) return { ...observation, isSynced: false };
            return equal(
              sentObservationFields(observation),
              sentObservationFields(sentObservation),
            )
              ? { ...observation, isSynced: true }
              : observation;
          }),
        };
      },
    ),
  };
}

/**
 * Marks unsent every part of `after` whose sent fields differ from `before`, or that
 * `before` doesn't have. A part whose sent fields are unchanged keeps the flags `after`
 * gives it, so a change that only records a part as sent or received keeps that.
 */
function withChangedPartsUnsent(
  before: InspectionModel,
  after: InspectionModel,
): InspectionModel {
  const categoriesBefore = new Map(
    before.inspectionCategoryTypes.map((categoryType) => [
      categoryType.externalId,
      categoryType,
    ]),
  );
  return {
    ...after,
    isSynced:
      after.isSynced &&
      equal(sentInspectionFields(after), sentInspectionFields(before)),
    inspectionCategoryTypes: after.inspectionCategoryTypes.map(
      (categoryType) => {
        const categoryBefore = categoriesBefore.get(categoryType.externalId);
        const observationsBefore = new Map(
          (categoryBefore?.observations ?? []).map((o) => [o.externalId, o]),
        );
        return {
          ...categoryType,
          isSynced:
            categoryType.isSynced &&
            !!categoryBefore &&
            equal(
              sentCategoryFields(categoryType),
              sentCategoryFields(categoryBefore),
            ),
          observations: categoryType.observations.map((observation) => {
            const observationBefore = observationsBefore.get(
              observation.externalId,
            );
            const unchanged =
              !!observationBefore &&
              equal(
                sentObservationFields(observation),
                sentObservationFields(observationBefore),
              );
            return unchanged
              ? observation
              : { ...observation, isSynced: false };
          }),
        };
      },
    ),
  };
}

export const inspectionNotStored = (externalId: string) =>
  new Error(`Inspection ${externalId} is not stored.`);

/**
 * The only writer of stored Inspections (#11590). Signing out deletes the whole local
 * database directly.
 */
class InspectionRecordService {
  deliveryState(inspection: InspectionModel): DeliveryState {
    return deliveryStateOf(inspection);
  }

  isEligibleForBackgroundSend(inspection: InspectionModel): boolean {
    return ELIGIBLE_FOR_BACKGROUND_SEND.has(deliveryStateOf(inspection));
  }

  /**
   * What background sync sends now: Queued and Photos pending, once the wait after
   * their last failure has passed.
   */
  isDueForBackgroundSend(inspection: InspectionModel, now: number): boolean {
    return (
      this.isEligibleForBackgroundSend(inspection) &&
      (inspection.sendFailures?.nextAttemptAt ?? 0) <= now
    );
  }

  /** What the pending count counts: everything submitted and not yet Delivered. */
  isPendingDelivery(inspection: InspectionModel): boolean {
    return PENDING_DELIVERY.has(deliveryStateOf(inspection));
  }

  /** Home's Submitted tab: these open read-only, and changing one needs Reopen. */
  isSubmitted(inspection: InspectionModel): boolean {
    return SUBMITTED.has(deliveryStateOf(inspection));
  }

  /**
   * Not Delivered: full sync and the observation download keep its local copy, and
   * signing out would lose it.
   */
  isUndelivered(inspection: InspectionModel): boolean {
    return deliveryStateOf(inspection) !== DeliveryState.Delivered;
  }

  /** The pending count, from the stored delivery state's index (#11657). */
  countPendingDelivery(): Promise<number> {
    return inspectionDB.inspections
      .where('deliveryState')
      .anyOf([...PENDING_DELIVERY])
      .count();
  }

  /** Queued and Photos pending, from the stored delivery state's index (#11657). */
  listEligibleForBackgroundSend(): Promise<InspectionModel[]> {
    return inspectionDB.inspections
      .where('deliveryState')
      .anyOf([...ELIGIBLE_FOR_BACKGROUND_SEND])
      .toArray();
  }

  /** What signing out would delete without it reaching the server. */
  countUndelivered(): Promise<number> {
    return inspectionDB.inspections
      .filter((inspection) => this.isUndelivered(inspection))
      .count();
  }

  /** Stores a new Inspection; fails if one with its external id is already stored. */
  async create(inspection: InspectionModel): Promise<void> {
    await inspectionDB.inspections.add(inspection);
  }

  /** Deletes the Inspection and its backup. */
  async delete(externalId: string): Promise<void> {
    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        await inspectionDB.inspections.delete(externalId);
        await inspectionDB.inspectionBackups.delete(externalId);
      },
    );
  }

  /** Deletes every Inspection and backup on the device. */
  async clear(): Promise<void> {
    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        await inspectionDB.inspections.clear();
        await inspectionDB.inspectionBackups.clear();
      },
    );
  }

  /**
   * Full sync's clear: deletes every Delivered Inspection, whose server copy the sync
   * brings back, and keeps the rest and every backup. Returns the Inspections kept.
   */
  async clearDelivered(): Promise<InspectionModel[]> {
    return inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const inspections = await inspectionDB.inspections.toArray();
        const delivered = inspections.filter(
          (inspection) => !this.isUndelivered(inspection),
        );
        await inspectionDB.inspections.bulkDelete(
          delivered.map((inspection) => inspection.externalId),
        );
        return inspections.filter((inspection) =>
          this.isUndelivered(inspection),
        );
      },
    );
  }

  /**
   * Keeps a snapshot of each of `parts` that an Inspection or backup has observations
   * on, before the parts change or leave the equipment's configuration. The snapshots
   * are never sent, so nothing is marked unsent. Returns the Inspections as they were
   * stored before.
   */
  async retainObservedFitments(
    parts: EquipmentPartModel[],
  ): Promise<InspectionModel[]> {
    return inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const inspections = await inspectionDB.inspections.toArray();
        const changedInspections = inspections
          .map((inspection) => retainObservedFitments(inspection, parts))
          .filter((inspection, index) => inspection !== inspections[index]);
        if (changedInspections.length) {
          await inspectionDB.inspections.bulkPut(changedInspections);
        }

        const backups = await inspectionDB.inspectionBackups.toArray();
        const changedBackups = backups
          .map((backup) => retainObservedFitments(backup, parts))
          .filter((backup, index) => backup !== backups[index]);
        if (changedBackups.length) {
          await inspectionDB.inspectionBackups.bulkPut(changedBackups);
        }
        return inspections;
      },
    );
  }

  /**
   * Removes a photo whose file is missing from the device. Its category is marked unsent,
   * so a submitted Inspection is sent again without it rather than counting as Delivered
   * while the server still holds the observation.
   */
  async removeMissingPhoto(
    externalId: string,
    observationExternalId: string,
  ): Promise<void> {
    await this.change(externalId, (inspection) => {
      for (const categoryType of inspection.inspectionCategoryTypes) {
        const index = categoryType.observations.findIndex(
          (o) => o.externalId === observationExternalId,
        );
        if (index !== -1) {
          categoryType.observations.splice(index, 1);
          categoryType.isSynced = false;
        }
      }
    });
  }

  /**
   * Moves the Inspection to Queued. A reopened one is marked unsent so it is sent again
   * even when nothing was edited. The backup is kept until the Inspection is Delivered.
   */
  async submit(externalId: string): Promise<void> {
    await inspectionDB.transaction('rw', inspectionDB.inspections, async () => {
      const current = await inspectionDB.inspections.get(externalId);
      if (!current) throw inspectionNotStored(externalId);
      await inspectionDB.inspections.put({
        ...current,
        submitRequested: true,
        // A fresh start: failures of an earlier version don't count against this one.
        sendFailures: undefined,
        reopened: false,
        isSynced: current.reopened ? false : current.isSynced,
      });
    });
  }

  /**
   * Records a failed send or photo upload of a submitted Inspection, and when background
   * sync may try it again. After 3 counted failures it is Needs attention. A failure of
   * an Inspection reopened meanwhile is not recorded: it belongs to the version that was
   * sent.
   */
  async recordSendFailure(
    externalId: string,
    { counted, reason }: SendFailure,
    at = Date.now(),
  ): Promise<void> {
    await this.change(externalId, (inspection) => {
      if (!PENDING_DELIVERY.has(deliveryStateOf(inspection))) return;
      const earlier = inspection.sendFailures;
      const attempts = (earlier?.attempts ?? 0) + 1;
      inspection.sendFailures = {
        counted: (earlier?.counted ?? 0) + (counted ? 1 : 0),
        attempts,
        nextAttemptAt: at + waitAfter(attempts),
        reason,
      };
    });
  }

  /** Retry: forgets the failures, so the Inspection can be sent straight away. */
  async retry(externalId: string): Promise<void> {
    await this.change(externalId, (inspection) => {
      inspection.sendFailures = undefined;
    });
  }

  /**
   * Takes a backup and marks the Inspection Reopened, from Queued, Photos pending, Needs
   * attention or Delivered, including while it is being sent. An In progress or already
   * Reopened Inspection is open for changes already, and keeps its backup.
   */
  async reopen(externalId: string): Promise<void> {
    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current) throw inspectionNotStored(externalId);
        if (!SUBMITTED.has(deliveryStateOf(current))) return;
        await inspectionDB.inspectionBackups.put(current);
        await inspectionDB.inspections.put({ ...current, reopened: true });
      },
    );
    await cacheInvalidationService.invalidateInspection(externalId);
  }

  /**
   * Puts a reopened Inspection back exactly as its backup, in the state it had before it
   * was reopened. The backup is deleted only once the restore has succeeded; a failure
   * throws and leaves both as they were.
   */
  async cancelReopen(externalId: string): Promise<void> {
    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current?.reopened) {
          throw new Error(`Inspection ${externalId} is not reopened.`);
        }
        const backup = await inspectionDB.inspectionBackups.get(externalId);
        if (!backup) {
          throw new Error(`Inspection ${externalId} has no backup to restore.`);
        }
        await inspectionDB.inspections.put(backup);
        await inspectionDB.inspectionBackups.delete(externalId);
      },
    );
    await cacheInvalidationService.invalidateInspection(externalId);
  }

  /**
   * Applies `edit` to the current record in one transaction. `edit` changes the copy it
   * is given; the parts whose sent fields it changed or added are marked unsent, so they
   * go with the next send. Which observation a field maps to is the caller's business.
   * Returns the record as stored, or null when it is not stored. An `edit` that throws
   * writes nothing.
   */
  async change(
    externalId: string,
    edit: (inspection: InspectionModel) => void,
  ): Promise<InspectionModel | null> {
    return inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current) return null;
        const edited = structuredClone(current);
        edit(edited);
        if (equal(edited, current)) return current;
        const changed = withChangedPartsUnsent(current, edited);
        await this.store(changed);
        return changed;
      },
    );
  }

  /**
   * Records that the server received a photo and holds it as sent. A photo replaced on
   * the device while it was uploading is left unsent, so the new one goes next time.
   */
  async recordPhotoReceived(
    externalId: string,
    uploaded: ObservationModel,
  ): Promise<void> {
    await this.change(externalId, (inspection) => {
      for (const categoryType of inspection.inspectionCategoryTypes) {
        const photo = categoryType.observations.find(
          (o) => o.externalId === uploaded.externalId,
        );
        if (photo?.value === uploaded.value) {
          photo.isSynced = true;
          photo.fileReceived = true;
        }
      }
    });
  }

  /** Takes the server's ids for the Inspection, its categories and observations. */
  async applyServerIds(
    externalId: string,
    syncItems: InspectionSyncItemsResponse,
  ): Promise<void> {
    await inspectionDB.transaction('rw', inspectionDB.inspections, async () => {
      const current = await inspectionDB.inspections.get(externalId);
      if (!current) return;
      const merged = mergeServerIdsIntoInspection(current, syncItems);
      if (merged) {
        await inspectionDB.inspections.put({ ...current, ...merged });
      }
    });
  }

  /** Renames converted photos, skipping any replaced on the device meanwhile. */
  async applyPhotoRenames(
    externalId: string,
    renames: PhotoRename[],
  ): Promise<void> {
    if (renames.length === 0) return;
    await inspectionDB.transaction('rw', inspectionDB.inspections, async () => {
      const current = await inspectionDB.inspections.get(externalId);
      if (!current) return;
      await inspectionDB.inspections.put({
        ...current,
        inspectionCategoryTypes: current.inspectionCategoryTypes.map(
          (categoryType) => ({
            ...categoryType,
            observations: categoryType.observations.map((observation) => {
              const rename = renames.find(
                (r) =>
                  r.observationExternalId === observation.externalId &&
                  r.from === observation.value,
              );
              return rename
                ? { ...observation, value: rename.to }
                : observation;
            }),
          }),
        ),
      });
    });
  }

  /** Sets the inspector's name. */
  async setInspectorName(externalId: string, name: string): Promise<void> {
    await inspectionDB.transaction('rw', inspectionDB.inspections, async () => {
      const current = await inspectionDB.inspections.get(externalId);
      if (!current) return;
      await inspectionDB.inspections.put({ ...current, inspectorName: name });
    });
  }

  /**
   * Applies what a rewrite of the observations changed, from `before` to `after`, to
   * the current record: observations it dropped are removed, and the fields it changed
   * are set. Anything else, including changes made since `before` was read, is kept.
   * Returns the record as stored.
   */
  async applyObservationRewrite(
    externalId: string,
    before: ObservationModel[],
    after: ObservationModel[],
  ): Promise<InspectionModel> {
    const beforeByExternalId = new Map(before.map((o) => [o.externalId, o]));
    const afterByExternalId = new Map(after.map((o) => [o.externalId, o]));
    return inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current) throw inspectionNotStored(externalId);
        const rewritten: InspectionModel = {
          ...current,
          inspectionCategoryTypes: current.inspectionCategoryTypes.map(
            (categoryType) => ({
              ...categoryType,
              observations: categoryType.observations.flatMap((observation) => {
                const was = beforeByExternalId.get(observation.externalId);
                if (!was) return [observation];
                const now = afterByExternalId.get(observation.externalId);
                if (!now) return [];
                const changedFields = (
                  Object.keys(now) as (keyof ObservationModel)[]
                ).filter((field) => !equal(was[field], now[field]));
                return [
                  {
                    ...observation,
                    ...Object.fromEntries(
                      changedFields.map((field) => [field, now[field]]),
                    ),
                  },
                ];
              }),
            }),
          ),
        };
        await inspectionDB.inspections.put(rewritten);
        return rewritten;
      },
    );
  }

  /**
   * Applies a successful send to the current record: the server's ids, and that the
   * record was accepted. Only the parts that still match `sent` are marked sent; anything
   * changed during the send stays unsent. An Inspection reopened during the send takes
   * only the ids, and so does its backup, so it stays Reopened with its edits.
   * Returns the record as stored, or null when it is no longer stored.
   */
  async applySendResult(
    externalId: string,
    sent: InspectionModel,
    syncItems: SyncSuccessItems | undefined,
  ): Promise<InspectionModel | null> {
    const accepted = applySyncSuccess(sent, syncItems, externalId);
    return inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current) return null;

        if (current.reopened) {
          const backup = await inspectionDB.inspectionBackups.get(externalId);
          if (backup) {
            await inspectionDB.inspectionBackups.put(
              withServerIds(backup, accepted),
            );
          }
          const withIds = withServerIds(current, accepted);
          await inspectionDB.inspections.put(withIds);
          return withIds;
        }

        const result: InspectionModel = {
          ...withSentPartsAccepted(withServerIds(current, accepted), sent),
          completed: accepted.completed,
          sendFailures: undefined,
        };
        await this.store(result);
        return result;
      },
    );
  }

  /**
   * Records that every photo reached the server. An Inspection reopened while it was
   * being sent stays Reopened, and the sent flags stay as the send result left them, so
   * a change made during the send still goes with the next send. The "submitted" marker
   * is kept: nothing outside this service reads it, and Delivered does not depend on it.
   */
  async finishDelivery(externalId: string): Promise<void> {
    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const current = await inspectionDB.inspections.get(externalId);
        if (!current || current.reopened) return;
        await this.store({
          ...current,
          completed: true,
          sendFailures: undefined,
        });
      },
    );
  }

  /**
   * Applies server copies from a sync or the observation download to the current
   * stored records. The local copy wins until an Inspection is Delivered; after that
   * the server copy replaces it.
   *
   * The observation download builds its copies on records it read earlier, and passes
   * those as `readCopies`: a record that changed after it was read (a send finished,
   * say) is left alone, and the next download applies the server's copy to it.
   * Returns the records written, by external id.
   */
  async applyServerCopies(
    serverCopies: InspectionModel[],
    readCopies?: ReadonlyMap<string, InspectionModel>,
  ): Promise<Map<string, InspectionModel>> {
    const written = new Map<string, InspectionModel>();
    if (serverCopies.length === 0) return written;

    await inspectionDB.transaction(
      'rw',
      inspectionDB.inspections,
      inspectionDB.inspectionBackups,
      async () => {
        const storedRecords = await inspectionDB.inspections.bulkGet(
          serverCopies.map((serverCopy) => serverCopy.externalId),
        );
        serverCopies.forEach((serverCopy, index) => {
          const local = storedRecords[index];
          const readCopy = readCopies?.get(serverCopy.externalId);
          if (readCopy && !equal(local, readCopy)) return;
          written.set(
            serverCopy.externalId,
            local && this.isUndelivered(local)
              ? applyServerIdsAndPhotoReceipts(local, serverCopy)
              : serverCopy,
          );
        });
        for (const record of written.values()) {
          await this.store(record);
        }
      },
    );
    return written;
  }

  /**
   * Stores a record inside the caller's transaction, which must include the backups.
   * The backup is kept until the Inspection is Delivered.
   */
  private async store(record: InspectionModel): Promise<void> {
    await inspectionDB.inspections.put(record);
    if (deliveryStateOf(record) === DeliveryState.Delivered) {
      await inspectionDB.inspectionBackups.delete(record.externalId);
    }
  }
}

export const inspectionRecordService = new InspectionRecordService();
